import React from "react";
import { useLocation } from "react-router-dom";
import Icon from "../components/AppIcon";
import { logoSrc } from "../api/client";

// ---------------------------------------------------------------------------
// Candidate post-interview thank-you (/business/interview-complete).
//
// This is where a candidate lands the moment their interview ends. It is the
// deterministic branded confirmation — shown regardless of backend scoring state —
// and is reached via the generating-result divert (see pages/generating-result),
// which fires for BOTH the manual "End interview" and the auto-timeout paths.
//
// Privacy: a candidate must NEVER see a score or report. This page shows only a
// thank-you; it makes no report request and renders no result. Branding (company
// name / logo / job title) comes from the navigation state the divert passes; a
// hard refresh with no state simply falls back to a generic thank-you.
// ---------------------------------------------------------------------------

export default function InterviewComplete() {
  const location = useLocation();
  const s = location.state || {};
  const companyName = s.companyName || "";
  const jobTitle = s.jobTitle || "";
  const logo = logoSrc(s.companyLogoUrl);

  return (
    <div className="min-h-screen bg-background px-4 py-10 sm:py-16">
      <div className="mx-auto flex w-full max-w-lg flex-col items-center">
        {/* Company brand */}
        <div className="mb-8 flex flex-col items-center text-center">
          {logo ? (
            <div className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-border bg-card p-2">
              <img
                src={logo}
                alt={companyName ? `${companyName} logo` : "Company logo"}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Icon name="Building2" size={28} />
            </div>
          )}
          <p className="text-lg font-semibold text-foreground">
            {companyName || "Your interview"}
          </p>
        </div>

        <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/15 text-success">
              <Icon name="CheckCircle2" size={30} />
            </div>
            <h1 className="text-2xl font-semibold text-foreground">Interview complete</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Thank you for completing your interview
              {jobTitle ? (
                <>
                  {" "}
                  for the <span className="font-medium text-foreground">{jobTitle}</span> role
                </>
              ) : null}
              {companyName ? (
                <>
                  {" "}
                  with <span className="font-medium text-foreground">{companyName}</span>
                </>
              ) : null}
              . Your responses have been submitted successfully.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              The hiring team will review your interview and reach out with next
              steps. There&apos;s nothing more you need to do — you can safely close
              this window.
            </p>
          </div>
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Powered by{" "}
          <span className="font-semibold">
            prepx<span className="text-primary">ai</span>
          </span>
        </p>
      </div>
    </div>
  );
}
